import { apiRequest } from './api';
import { getApiUrl, getApiUrlWithParams } from '../config/api';

export const financialApi = {
  // Phase 1: Dashboard & Financial Control
  getDashboard: async (year, month) => {
    // Expected to return kpis, urgent_actions, recent_transactions
    // year / month: ماه شمسی انتخابی (بدون مقدار = ماه جاری)
    const params = {};
    if (year) params.period_year = year;
    if (month) params.period_month = month;
    const url = Object.keys(params).length > 0
      ? getApiUrlWithParams('financial/dashboard', params)
      : getApiUrl('financial/dashboard');
    return apiRequest(url, { method: 'GET' });
  },

  getProfitAndLoss: async (startDate, endDate) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    const url = Object.keys(params).length > 0 
      ? getApiUrlWithParams('financial/profit-and-loss', params)
      : getApiUrl('financial/profit-and-loss');
      
    return apiRequest(url, { method: 'GET' });
  },

  getBudgetVsActual: async (year, month) => {
    const params = {};
    if (year) params.period_year = year;
    if (month) params.period_month = month;
    
    const url = Object.keys(params).length > 0
      ? getApiUrlWithParams('financial/budgets/vs-actual', params)
      : getApiUrl('financial/budgets/vs-actual');
      
    return apiRequest(url, { method: 'GET' });
  },

  getCashflowForecast: async () => {
    const url = getApiUrl('financial/cashflow-forecast');
    return apiRequest(url, { method: 'GET' });
  },

  // Phase 2: Approvals & Transaction Center
  getPendingApprovals: async () => {
    const url = getApiUrl('financial/approvals/pending');
    return apiRequest(url, { method: 'GET' });
  },

  decidePayment: async (paymentId, decisionData) => {
    const url = getApiUrl(`financial/approvals/${paymentId}/decide`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(decisionData),
    });
  },

  updateApprovalPayment: async (paymentId, data) => {
    const url = getApiUrl(`financial/approvals/${paymentId}/update`);
    if (data instanceof FormData) {
      return apiRequest(url, {
        method: 'POST',
        body: data,
      });
    }
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getDealsForPayment: async () => {
    const url = getApiUrl('financial/deals-for-payment');
    return apiRequest(url, { method: 'GET' });
  },

  registerDealPayment: async (dealId, formData) => {
    const url = getApiUrl(`financial/deals/${dealId}/payments`);
    // formData is expected to be a FormData object since it contains a file
    return apiRequest(url, {
      method: 'POST',
      body: formData,
      // Omit Content-Type to let the browser set it with the correct boundary for multipart/form-data
      headers: {
        'Accept': 'application/json',
      }
    });
  },

  resubmitDealPayment: async (dealId, paymentId, formData) => {
    const url = getApiUrl(`financial/deals/${dealId}/payments/${paymentId}/resubmit`);
    return apiRequest(url, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json',
      }
    });
  },

  matchWebOrder: async (orderData) => {
    const url = getApiUrl('financial/match-web-order');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  globalSearch: async (q) => {
    const url = getApiUrlWithParams('financial/search', { q });
    return apiRequest(url, { method: 'GET' });
  },

  getTransactions: async (params) => {
    const url = Object.keys(params || {}).length > 0
      ? getApiUrlWithParams('financial/transactions', params)
      : getApiUrl('financial/transactions');
    return apiRequest(url, { method: 'GET' });
  },

  getMonthlyChecklist: async (year, month) => {
    const url = getApiUrlWithParams('financial/monthly-checklist', { period_year: year, period_month: month });
    return apiRequest(url, { method: 'GET' });
  },

  closeMonthlyChecklist: async (year, month, note) => {
    const url = getApiUrl('financial/monthly-checklist/close');
    return apiRequest(url, { method: 'POST', body: JSON.stringify({ period_year: year, period_month: month, note }) });
  },

  reopenMonthlyChecklist: async (year, month) => {
    const url = getApiUrl('financial/monthly-checklist/reopen');
    return apiRequest(url, { method: 'POST', body: JSON.stringify({ period_year: year, period_month: month }) });
  },

  getAuditLogs: async (params) => {
    const url = Object.keys(params || {}).length > 0
      ? getApiUrlWithParams('financial/audit-logs', params)
      : getApiUrl('financial/audit-logs');
    return apiRequest(url, { method: 'GET' });
  },

  // Phase 3: Receivables, Installments & Cheques
  generateInstallments: async (dealId, data) => {
    const url = getApiUrl(`financial/deals/${dealId}/installments`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getDealInstallments: async (dealId) => {
    const url = getApiUrl(`financial/deals/${dealId}/installments`);
    return apiRequest(url, { method: 'GET' });
  },

  getCheques: async (params) => {
    const url = Object.keys(params || {}).length > 0
      ? getApiUrlWithParams('financial/cheques', params)
      : getApiUrl('financial/cheques');
    return apiRequest(url, { method: 'GET' });
  },

  getChequeFormData: async () => {
    const url = getApiUrl('financial/cheques/form-data');
    return apiRequest(url, { method: 'GET' });
  },

  getClientDeals: async (clientId) => {
    const url = getApiUrl(`clients/${clientId}/deals`);
    return apiRequest(url, { method: 'GET' });
  },

  registerCheque: async (data) => {
    const url = getApiUrl('financial/cheques');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  changeChequeStatus: async (id, data) => {
    const url = getApiUrl(`financial/cheques/${id}/status`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getChequeHistory: async (id) => {
    const url = getApiUrl(`financial/cheques/${id}/history`);
    return apiRequest(url, { method: 'GET' });
  },

  addChequeHistory: async (id, data) => {
    const url = getApiUrl(`financial/cheques/${id}/history`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getCheque: async (id) => {
    const url = getApiUrl(`financial/cheques/${id}`);
    return apiRequest(url, { method: 'GET' });
  },

  updateCheque: async (id, data) => {
    const url = getApiUrl(`financial/cheques/${id}`);
    return apiRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteCheque: async (id) => {
    const url = getApiUrl(`financial/cheques/${id}`);
    return apiRequest(url, { method: 'DELETE' });
  },

  bulkDeleteCheques: async (ids) => {
    const url = getApiUrl('financial/cheques/bulk-delete');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  },

  bulkChangeChequeStatus: async (data) => {
    const url = getApiUrl('financial/cheques/bulk-status');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getReceivablesSummary: async () => {
    const url = getApiUrl('financial/receivables/summary');
    return apiRequest(url, { method: 'GET' });
  },

  getAgingReport: async () => {
    const url = getApiUrl('financial/receivables/aging');
    return apiRequest(url, { method: 'GET' });
  },

  // Phase 6: Refunds, Discounts & Client 360
  getRefunds: async (params) => {
    const url = Object.keys(params || {}).length > 0 
      ? getApiUrlWithParams('financial/refunds', params)
      : getApiUrl('financial/refunds');
    return apiRequest(url, { method: 'GET' });
  },

  createRefund: async (data) => {
    const url = getApiUrl('financial/refunds');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  approveRefund: async (id) => {
    const url = getApiUrl(`financial/refunds/${id}/approve`);
    return apiRequest(url, { method: 'POST' });
  },

  executeRefund: async (id, data) => {
    const url = getApiUrl(`financial/refunds/${id}/execute`);
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  setDiscountLimit: async (data) => {
    const url = getApiUrl('financial/discount-limits');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  validateDiscount: async (data) => {
    const url = getApiUrl('financial/validate-discount');
    return apiRequest(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getClientFinancialProfile: async (clientId) => {
    const url = getApiUrl(`financial/clients/${clientId}/financial-profile`);
    return apiRequest(url, { method: 'GET' });
  },

  // Commission & Targets & Payroll
  getTargets: async (params) => {
    const url = Object.keys(params || {}).length > 0
      ? getApiUrlWithParams('financial/targets', params)
      : getApiUrl('financial/targets');
    return apiRequest(url, { method: 'GET' });
  },

  getCommissionPlans: async (params) => {
    const url = Object.keys(params || {}).length > 0
      ? getApiUrlWithParams('financial/commission-plans', params)
      : getApiUrl('financial/commission-plans');
    return apiRequest(url, { method: 'GET' });
  },

  getPayrolls: async (params) => {
    const url = Object.keys(params || {}).length > 0
      ? getApiUrlWithParams('financial/payroll', params)
      : getApiUrl('financial/payroll');
    return apiRequest(url, { method: 'GET' });
  },

  getPettyCash: async (params) => {
    const url = Object.keys(params || {}).length > 0
      ? getApiUrlWithParams('financial/petty-cash', params)
      : getApiUrl('financial/petty-cash');
    return apiRequest(url, { method: 'GET' });
  },
};
